import { useEffect, useRef, useCallback } from "react";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { useRealtimeChannel } from "./useRealtimeChannel";
import { SUPPORTED_LEAGUES } from "../lib/constants";

// The Wire client hook (spec docs/specs/2026-08-09-news-feed.md §PR 3). Single job:
// subscribe to the cron-owned `news` table via Realtime and hand App.js the newest
// WIRE_LIMIT headlines. Mirrors useGameCacheRealtime (seed + channel + onRecover
// reseed) minus the date window / dataSource / midnight timer — a news feed is a
// flat recency list, not a per-day scoreboard.

const WIRE_LIMIT = 6;

// news row -> client wire item. DISPLAY-ONLY. `affectsSlips` is deliberately never
// set (spec §Non-goals): the HomePage pill stays dormant until slip-matching v2.
// Fail-closed: a row missing an id, league, or headline is dropped (return null)
// rather than reaching render — the DB enforces NOT NULL/CHECK, but a raw Realtime
// payload is untrusted, and a null league would otherwise throw in the byline. `url`
// stays optional (a null-url card is shown but simply not tappable).
function transformRow(row) {
  if (!row || row.id == null || !row.league || !row.headline) return null;
  return {
    id: row.id,
    league: row.league,
    headline: row.headline,
    url: row.url,
    publishedAt: row.published_at,
  };
}

// Sort key: epoch ms, or -Infinity for a null/malformed published_at so a bad
// timestamp sinks to the bottom instead of returning NaN from the comparator (a
// NaN comparator scrambles Array.sort into an implementation-defined order).
function publishedTs(item) {
  const t = new Date(item?.publishedAt).getTime();
  return isNaN(t) ? -Infinity : t;
}

// Newest first, then cap. Single source of ordering for seed + events.
function sortAndCap(items) {
  return items
    .slice()
    .sort((a, b) => publishedTs(b) - publishedTs(a))
    .slice(0, WIRE_LIMIT);
}

/**
 * @param {Object} params
 * @param {Function} params.setWire - setter for the wire array
 * @param {boolean} [params.enabled=true] - gate seed + subscription on auth readiness.
 *   Pass Boolean(user): the `news` read policy is authenticated-only, so seeding
 *   before a session attaches (cold start / guest) silently returns [] (RLS empty)
 *   and — because the mount effect never re-runs — the feed stays empty until the
 *   next 30-min ingest event. Gating on auth reseeds exactly once when it arrives.
 * @returns {{ refresh: Function }}
 */
export function useWireRealtime({ setWire, enabled = true }) {
  const wireRef = useRef([]);

  const seed = useCallback(async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from("news")
        .select("*")
        .in("league", SUPPORTED_LEAGUES)
        .order("published_at", { ascending: false })
        .limit(WIRE_LIMIT);

      if (error) {
        const err = new Error(error.message || "news seed query failed");
        err.code = error.code;
        err.details = error.details;
        err.hint = error.hint;
        throw err;
      }

      // Seed is AUTHORITATIVE: the DB snapshot (mount or post-recovery) replaces the
      // in-memory list, so a row deleted/pruned while the channel was down is
      // reconciled away rather than lingering. Realtime events after the seed keep
      // it live; the brief seed-query window during recovery is DB-truth by design.
      const wire = sortAndCap((data || []).map(transformRow).filter(Boolean));
      wireRef.current = wire;
      setWire(wire);
    } catch (err) {
      Sentry.captureException(err, { tags: { function: "useWireRealtime", stage: "seed" } });
    }
  }, [setWire]);

  const buildChannel = useCallback(
    (channel) => {
      const handler = (payload) => {
        try {
          const { eventType } = payload;
          let items = [...wireRef.current];

          if (eventType === "DELETE") {
            const oldId = payload.old?.id;
            items = items.filter((w) => String(w.id) !== String(oldId));
          } else {
            const transformed = transformRow(payload.new);
            if (transformed) {
              const idx = items.findIndex((w) => String(w.id) === String(transformed.id));
              if (idx >= 0) items[idx] = transformed;
              else items.push(transformed);
            }
          }

          const wire = sortAndCap(items);
          wireRef.current = wire;
          setWire(wire);
        } catch (err) {
          Sentry.captureException(err, {
            tags: { function: "useWireRealtime", stage: "realtime-event" },
          });
        }
      };

      // INSERT/UPDATE are league-filtered. DELETE is registered WITHOUT a filter:
      // Supabase Postgres Changes does not apply row filters to DELETE (the old
      // record carries only the replica-identity PK), so a filtered DELETE would
      // silently never deliver the cron's 14-day retention prunes and aged-out
      // stories would linger. The news table CHECK already restricts leagues; the
      // handler only removes ids already in the list, so unrelated deletes no-op.
      const base = { schema: "public", table: "news" };
      const filter = `league=in.(${SUPPORTED_LEAGUES.join(",")})`;
      return channel
        .on("postgres_changes", { event: "INSERT", ...base, filter }, handler)
        .on("postgres_changes", { event: "UPDATE", ...base, filter }, handler)
        .on("postgres_changes", { event: "DELETE", ...base }, handler);
    },
    [setWire]
  );

  // Reseed after the replacement channel is live (Codex P1): useRealtimeChannel
  // fires onRecover BEFORE tear-down + backoff + resubscribe, so seeding there would
  // miss any mutation during the disconnected gap. onSubscribed runs after the
  // (re)subscription is confirmed. Skip the FIRST subscribe — the mount effect below
  // owns the initial seed — and reseed authoritatively on every reconnect after that.
  const seededOnceRef = useRef(false);
  const onSubscribed = useCallback(() => {
    if (!seededOnceRef.current) {
      seededOnceRef.current = true;
      return;
    }
    seed();
  }, [seed]);

  useRealtimeChannel({
    enabled,
    channelName: "wire",
    sentryContext: { function: "useWireRealtime", channel: "wire" },
    buildChannel,
    onSubscribed,
  });

  // Seed once when enabled (auth ready). Re-runs when enabled flips false->true so a
  // post-login mount reseeds. No midnight timer (unlike useGameCacheRealtime — no
  // date window).
  useEffect(() => {
    if (!supabase || !enabled) return;
    seed();
  }, [seed, enabled]);

  return { refresh: seed };
}
